import { useTranslation } from "react-i18next";
import { Pressable, StyleSheet } from "react-native";
import { Icon, IconButton } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box, { type BoxProps } from "../Box";

type PlaybackControlsProps = {
	isPlaying: boolean;
	isFirstTrack: boolean;
	isLastTrack: boolean;
	compact?: boolean;
	onPlayPause: () => void;
	onSkipPrevious: () => void;
	onSkipNext: () => void;
	onRewind: () => void;
	onForward: () => void;
};

const PlaybackControls = ({
	isPlaying,
	isFirstTrack,
	isLastTrack,
	compact,
	onPlayPause,
	onSkipPrevious,
	onSkipNext,
	onRewind,
	onForward,
}: PlaybackControlsProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const iconSize = compact ? 18 : 24;
	const playIconSize = compact ? 18 : 24;
	const playCircleSize = compact ? 36 : 40;
	const buttonStyle = compact
		? styles.controlButtonCompact
		: styles.controlButton;
	const playOuterStyle = compact
		? styles.playButtonOuterCompact
		: styles.playButtonOuter;
	const containerProps: BoxProps = compact
		? { justifyContent: "center", gap: 20, padding: 0, borderWidth: 0 }
		: {
				justifyContent: "space-between",
				padding: 8,
				borderRadius: theme.roundness * 100,
				backgroundColor: theme.colors.surface,
				borderWidth: 1,
				borderColor: theme.colors.outlineVariant,
				width: "100%",
				maxWidth: 343,
				alignSelf: "center",
			};

	return (
		<Box flexDirection="row" alignItems="center" {...containerProps}>
			<IconButton
				icon="skip-previous"
				size={iconSize}
				onPress={onSkipPrevious}
				disabled={isFirstTrack}
				iconColor={theme.colors.onSurfaceVariant}
				style={buttonStyle}
				accessibilityLabel={t("podcast.a11y.skipPrevious")}
			/>
			<IconButton
				icon="rewind-10"
				size={iconSize}
				onPress={onRewind}
				iconColor={theme.colors.onSurfaceVariant}
				style={buttonStyle}
				accessibilityLabel={t("podcast.a11y.rewind", { seconds: 10 })}
			/>
			<Pressable
				onPress={onPlayPause}
				style={playOuterStyle}
				accessibilityRole="button"
				accessibilityLabel={
					isPlaying ? t("podcast.a11y.pause") : t("podcast.a11y.play")
				}
			>
				<Box
					width={playCircleSize}
					height={playCircleSize}
					borderRadius={theme.roundness * 100}
					backgroundColor={theme.colors.primary}
					alignItems="center"
					justifyContent="center"
				>
					<Icon
						source={isPlaying ? "pause" : "play"}
						size={playIconSize}
						color={theme.colors.onPrimary}
					/>
				</Box>
			</Pressable>
			<IconButton
				icon="fast-forward-30"
				size={iconSize}
				onPress={onForward}
				iconColor={theme.colors.onSurfaceVariant}
				style={buttonStyle}
				accessibilityLabel={t("podcast.a11y.fastForward", { seconds: 30 })}
			/>
			<IconButton
				icon="skip-next"
				size={iconSize}
				onPress={onSkipNext}
				disabled={isLastTrack}
				iconColor={theme.colors.onSurfaceVariant}
				style={buttonStyle}
				accessibilityLabel={t("podcast.a11y.skipNext")}
			/>
		</Box>
	);
};

const styles = StyleSheet.create({
	playButtonOuter: {
		width: 48,
		height: 48,
		alignItems: "center",
		justifyContent: "center",
	},
	playButtonOuterCompact: {
		width: 36,
		height: 36,
		alignItems: "center",
		justifyContent: "center",
	},
	controlButton: {
		margin: 0,
		width: 48,
		height: 48,
	},
	controlButtonCompact: {
		margin: 0,
		width: 36,
		height: 36,
	},
});

export default PlaybackControls;
